import { computed, reactive, ref } from 'vue'
import { useAuthStore } from '@/features/auth'
import { useWorkspaceStore } from '@/store/workspace'
import {
  executePublish,
  resolvePublishContext,
  runPublishGateChecks,
  runPublishChecks,
  type PublishFlowHooks,
} from '@/publish/services/submitPublish'
import { submitGameTags } from '@/publish/services/tagSetup'
import { clearStoredTagDraft, readStoredTagDraft, saveStoredTagDraft } from '@/publish/services/tagSetupBridge'
import { calculatePublishFlowerUnlock } from '@/publish/services/calculateFlowerUnlock'
import { sanitizeSignedUrl } from '@/utils/sanitizeLog'
import { PROJECT_CURRENT_PAY_TYPE } from '@/features/project-cloud/ossPaths'
import BrowserUtil from '@/platform/oss/browserUtil'
import type {
  PublishCheckItem,
  PublishContext,
  PublishFormModel,
  PublishProgressItem,
  PublishResult,
  PublishPayType,
  PublishRawTagList,
  PublishStepMeta,
  PublishTagSetupState,
} from '@/publish/types'

const STEP_LABELS: PublishStepMeta[] = [
  { key: 'context', label: '读取发布信息', api: 'get_game_version / get_pkgquery', failMessage: '读取发布信息失败，请检查网络或登录状态' },
  { key: 'checks', label: '发布前检查', api: 'check_black_words_tool / check_proj_auth / check_if_add_tag', failMessage: '发布前检查未通过，请按提示修改后重试' },
  { key: 'project-name', label: '同步作品名', api: 'o_proj_modify.php', failMessage: '同步作品名失败，线上名称可能未更新' },
  { key: 'artifacts', label: '生成发布文件', api: '本地生成 Game.bin / Map.bin / MallNew.bin', failMessage: '生成发布文件失败，请检查工程资源是否缺失' },
  { key: 'snapshot-upload', label: '上传工程', api: 'initUpload / upload_url', failMessage: '上传工程失败，请重试发布' },
  { key: 'snapshot-queue', label: '等待工程处理', api: 'get_server_time / getQueueInfo', failMessage: '工程处理失败，请重试发布' },
  { key: 'snapshot-verify', label: '确认工程', api: 'get_project_file', failMessage: '工程确认失败，请重试发布' },
  { key: 'runtime-plan', label: '整理运行文件', api: '本地整理上传任务', failMessage: '整理运行文件失败，请检查发布文件' },
  { key: 'runtime-upload', label: '上传运行文件', api: 'initUpload / preCheck / upload_url', failMessage: '运行文件上传失败，请重试发布' },
  { key: 'runtime-queue', label: '等待运行文件处理', api: 'get_server_time / getQueueInfo', failMessage: '运行文件处理失败，请重试发布' },
  { key: 'map-confirm', label: '确认发布清单', api: 'get_putsign', failMessage: '发布清单确认失败，请重试发布' },
  { key: 'publish', label: '提交发布', api: 'get_game_path', failMessage: '提交发布失败，请稍后重试' },
  { key: 'request-log', label: '服务器通信', api: '当前接口请求', failMessage: '服务器通信失败，请重试发布' },
  { key: 'update-log', label: '写入简介或更新日志', api: 'add_update_log', failMessage: '发布成功，但简介或更新日志写入失败' },
  { key: 'post-save', label: '同步云端数据', api: '橙易云端保存 / ORG.pay', failMessage: '发布成功，但云端数据同步失败' },
  { key: 'game-attrs', label: '同步投稿设置', api: 'add_game_attr', failMessage: '发布成功，但投稿设置同步失败' },
  { key: 'tag-submit', label: '提交作品标签', api: 'add_game_tag', failMessage: '发布成功，但作品标签提交失败' },
  { key: 'cover-upload', label: '上传作品封面', api: 'upload_pic_sign / UpYun PUT', failMessage: '发布成功，但作品封面上传失败' },
]
const STEP_META_MAP = new Map(STEP_LABELS.map((item) => [item.key, item]))

const PAY_TYPE_STORAGE_PREFIX = 'avgmaker:publish:pay-type:'

function buildPayTypeStorageKey(guid: string): string {
  return `${PAY_TYPE_STORAGE_PREFIX}${guid.trim()}`
}

function buildPayTypeOssPath(guid: string): string {
  return `${guid.trim()}/${PROJECT_CURRENT_PAY_TYPE}`
}

function normalizePayType(value: unknown): PublishPayType {
  const payType = Number.parseInt(String(value ?? ''), 10)
  return payType === 1 || payType === 3 ? payType : 0
}

function readLocalPayType(guid: string): PublishPayType {
  return normalizePayType(localStorage.getItem(buildPayTypeStorageKey(guid)))
}

function saveLocalPayType(guid: string, payType: PublishPayType) {
  localStorage.setItem(buildPayTypeStorageKey(guid), String(payType))
}

async function readStoredPayType(guid: string, uid: number): Promise<PublishPayType> {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return 0
  const oss = BrowserUtil.getInstance()
  if (oss.initDefaultConfig().isConfigured()) {
    try {
      oss.setRootDir(String(uid))
      if (await oss.hasObject(buildPayTypeOssPath(normalizedGuid))) {
        const payType = normalizePayType(await oss.getText(buildPayTypeOssPath(normalizedGuid)))
        saveLocalPayType(normalizedGuid, payType)
        return payType
      }
      localStorage.removeItem(buildPayTypeStorageKey(normalizedGuid))
      return 0
    } catch {
    }
  }
  return readLocalPayType(normalizedGuid)
}

async function saveStoredPayType(guid: string, uid: number, payType: PublishPayType): Promise<void> {
  const normalizedGuid = guid.trim()
  if (!normalizedGuid) return
  saveLocalPayType(normalizedGuid, payType)
  const oss = BrowserUtil.getInstance()
  if (!oss.initDefaultConfig().isConfigured()) return
  oss.setRootDir(String(uid))
  await oss.writeData(buildPayTypeOssPath(normalizedGuid), String(payType), {
    contentType: 'text/plain;charset=utf-8',
  })
}

function formatRequestProgress(status: PublishProgressItem['status']): string {
  if (status === 'running') return '正在与服务器同步'
  if (status === 'done') return '服务器同步完成'
  if (status === 'error') return '服务器同步失败'
  return ''
}

function getUploadProgressLabel(fileName: string): string {
  const lower = String(fileName || '').toLowerCase()
  if (lower.includes('projects')) return '工程快照'
  if (lower.endsWith('map.bin')) return '发布清单'
  if (lower.includes('/bin/') || lower.startsWith('bin/')) return '运行时文件'
  if (lower.includes('/other/') || lower.startsWith('other/')) return '附加资源'
  return '发布文件'
}

function getUploadStatusText(status: string): string {
  if (status === 'hashing') return '正在校验文件'
  if (status === 'uploading') return '正在上传'
  if (status === 'completed') return '上传完成'
  if (status === 'error') return '上传失败'
  return status || '处理中'
}

function sanitizePublishDetail(value: unknown): string {
  return sanitizeSignedUrl(String(value ?? ''))
    .replace(/[A-Za-z0-9_-]+\/other\/ProjectS\d+\.bin/gi, '工程快照')
    .replace(/ProjectS\d*\.bin/gi, '工程快照')
    .replace(/Data\/ProjectS\.bin/gi, '工程快照')
    .replace(/bin\/Map\.bin/gi, '发布清单')
    .replace(/Map\.bin/gi, '发布清单')
    .replace(/Data\/Game\.bin|bin\/Game\.bin|Game\.bin/gi, '运行时主包')
    .replace(/Data\/Var\.bin|bin\/Var\.bin|Var\.bin/gi, '变量数据')
    .replace(/Data\/MallNew\.bin|bin\/MallNew\.bin|MallNew\.bin/gi, '商城数据')
    .replace(/\b[a-f0-9]{32}\b/gi, '校验值')
}

function formatProgressError(key: string, rawDetail: string): string {
  const meta = STEP_META_MAP.get(key)
  const detail = sanitizePublishDetail(rawDetail).trim()
  const lines = [
    `失败位置：${meta?.label ?? key}`,
    `对应接口：${meta?.api ?? '未知接口'}`,
  ]
  if (detail) {
    lines.push(`服务器提示：${detail}`)
  } else if (meta?.failMessage) {
    lines.push(`服务器提示：${meta.failMessage}`)
  }
  return lines.join('\n')
}

export function usePublishPanel() {
  const auth = useAuthStore()
  const workspace = useWorkspaceStore()
  const loadingContext = ref(false)
  const runningChecks = ref(false)
  const publishing = ref(false)
  const publishLocked = ref(false)
  const context = ref<PublishContext | null>(null)
  const checks = ref<PublishCheckItem[]>([])
  const result = ref<PublishResult | null>(null)
  const uploadMessage = ref('')
  const tagSelection = ref<PublishRawTagList | null>(null)
  const tagSetupState = ref<PublishTagSetupState>('none')
  const tagWebOpen = ref(false)
  const openAfterTagSetup = ref(false)
  const form = reactive<PublishFormModel>({
    projectName: workspace.project?.storyTree.ProjectName?.trim() || '',
    synopsis: '',
    versionNote: '',
    payType: 0,
    flowerUnlock: 0,
    hpNumber: 0,
    isFinished: false,
    canSyncGameAttrs: true,
    activityTid: -1000,
    screenType: 0,
    copyrightAction: '0',
    copyrightOwnerId: -1000,
    copyrightWorkId: -1000,
  })

  const progressMap = reactive<Record<string, PublishProgressItem>>(
    Object.fromEntries(
      STEP_LABELS.map((item) => [
        item.key,
        {
          key: item.key,
          label: item.label,
          status: 'pending',
          detail: '',
        } satisfies PublishProgressItem,
      ]),
    ),
  )

  const progressItems = computed(() => STEP_LABELS.map((item) => progressMap[item.key]!))
  const hasBlockingChecks = computed(() => checks.value.some((item) => item.level === 'error'))

  const hooks: PublishFlowHooks = {
    onStep(key, status, detail) {
      const safeDetail = detail == null ? detail : sanitizePublishDetail(detail)
      const meta = STEP_META_MAP.get(key)
      if (!progressMap[key]) {
        progressMap[key] = { key, label: meta?.label ?? key, status, detail: safeDetail }
        return
      }
      progressMap[key].status = status
      progressMap[key].detail = safeDetail
    },
    onUploadProgress(fileName, percent, status) {
      uploadMessage.value = `${getUploadProgressLabel(fileName)} · ${getUploadStatusText(status)} · ${percent}%`
    },
    onRequest(status, rawDetail) {
      const detail = status === 'error' && rawDetail
        ? sanitizePublishDetail(rawDetail)
        : formatRequestProgress(status)
      if (!progressMap['request-log']) {
        progressMap['request-log'] = { key: 'request-log', label: '服务器通信', status, detail }
        return
      }
      progressMap['request-log'].status = status
      progressMap['request-log'].detail = detail
    },
  }

  function resetTransientState() {
    checks.value = []
    result.value = null
    uploadMessage.value = ''
    for (const item of Object.values(progressMap)) {
      item.status = 'pending'
      item.detail = ''
    }
  }

  async function syncStoredTagDraft(nextContext: PublishContext | null) {
    const guid = nextContext?.guid?.trim()
    if (!guid) {
      tagSelection.value = null
      tagSetupState.value = 'none'
      return
    }
    const stored = await readStoredTagDraft(guid, auth.uid)
    tagSelection.value = stored?.selection ?? null
    tagSetupState.value = stored?.state ?? 'none'
  }

  async function persistCurrentTagDraft(nextContext?: PublishContext | null) {
    const guid = nextContext?.guid?.trim() || context.value?.guid?.trim()
    if (!guid || !tagSelection.value || tagSetupState.value === 'none') return
    await saveStoredTagDraft(guid, auth.uid, {
      selection: tagSelection.value,
      state: tagSetupState.value,
    })
  }

  function syncFlowerUnlockFromProject(storedPayType = form.payType) {
    if (!workspace.project) return
    const result = calculatePublishFlowerUnlock(workspace.project, storedPayType)
    form.payType = result.payType
    form.flowerUnlock = result.flowerUnlock
    workspace.project.projectConstant.userFowers = result.flowerUnlock
  }

  async function initializeContext(force = false, syncProjectName = true) {
    if (!force && context.value) {
      if (syncProjectName) {
        form.projectName = workspace.project?.storyTree.ProjectName?.trim() || form.projectName
      }
      return
    }
    loadingContext.value = true
    resetTransientState()
    progressMap.context.status = 'running'
    try {
      if (syncProjectName) {
        form.projectName = workspace.project?.storyTree.ProjectName?.trim() || form.projectName
      }
      context.value = await resolvePublishContext(hooks)
      await syncStoredTagDraft(context.value)
      if (syncProjectName) {
        form.payType = context.value.gindex ? await readStoredPayType(context.value.guid, auth.uid) : 0
        form.flowerUnlock = 0
      }
      progressMap.context.status = 'done'
      progressMap.context.detail = `线上 ${context.value.serverCurrentVersion}，发布 ${context.value.publishVersion}`
    } catch (error) {
      context.value = null
      await syncStoredTagDraft(null)
      progressMap.context.status = 'error'
      progressMap.context.detail = error instanceof Error ? sanitizePublishDetail(error.message) : '发布上下文加载失败'
      throw error
    } finally {
      loadingContext.value = false
    }
  }

  async function runChecksAction(options?: { skipGateChecks?: boolean }) {
    if (!context.value) {
      await initializeContext()
    }
    if (!context.value) return
    runningChecks.value = true
    progressMap.checks.status = 'running'
    try {
      checks.value = await runPublishChecks(
        form,
        context.value,
        {
          stagedTagSelection: tagSelection.value,
          tagSelectionSubmitted: tagSetupState.value === 'submitted',
          skipGateChecks: options?.skipGateChecks,
        },
        hooks,
      )
      progressMap.checks.status = checks.value.some((item) => item.level === 'error') ? 'error' : 'done'
      progressMap.checks.detail = checks.value.length ? `共 ${checks.value.length} 项检查结果` : '未发现阻塞项'
    } finally {
      runningChecks.value = false
    }
  }

  async function publishAction() {
    if (publishLocked.value) return
    publishLocked.value = true
    try {
      await initializeContext(false, false)
      if (!context.value) return
      await runChecksAction({ skipGateChecks: true })
      if (hasBlockingChecks.value) {
        const blocking = checks.value.find((item) => item.level === 'error')
        throw new Error(blocking?.message || blocking?.title || '发布前检查未通过')
      }
      publishing.value = true
      const publishResult = await executePublish(form, context.value, hooks, {
        pendingTagSelection: tagSetupState.value === 'submitted' ? null : tagSelection.value,
      })
      result.value = publishResult
      try {
        await saveStoredPayType(context.value.guid, auth.uid, form.payType)
      } catch (error) {
        progressMap['post-save'].status = 'error'
        progressMap['post-save'].detail = error instanceof Error ? `收费方式保存失败：${error.message}` : '收费方式保存失败'
      }
      form.versionNote = ''
      const nextGindex = publishResult.gindex ?? context.value.gindex
      context.value = {
        ...context.value,
        localCurrentVersion: publishResult.publishVersion,
        serverCurrentVersion: publishResult.publishVersion,
        publishVersion: String((Number.parseInt(publishResult.publishVersion, 10) || 0) + 1),
        gindex: nextGindex,
        isFirstPublish: false,
      }
      if (publishResult.tagSubmitted && context.value.guid) {
        tagSelection.value = null
        tagSetupState.value = 'none'
        await clearStoredTagDraft(context.value.guid, auth.uid)
      } else if (tagSelection.value) {
        tagSetupState.value = 'staged'
        await persistCurrentTagDraft(context.value)
      }
      workspace.syncProjectDisplayName()
      form.projectName = workspace.project?.storyTree.ProjectName?.trim() || form.projectName
    } catch (error) {
      const activeItem = [...progressItems.value].reverse().find((item) => item.key !== 'request-log' && item.status === 'running')
      const target = activeItem ?? progressMap.publish
      const requestErrorDetail = progressMap['request-log']?.status === 'error'
        ? progressMap['request-log'].detail
        : ''
      target.status = 'error'
      const errorDetail = error instanceof Error ? error.message : ''
      target.detail = formatProgressError(target.key, errorDetail || requestErrorDetail || '发布失败')
      throw error
    } finally {
      publishing.value = false
      publishLocked.value = false
    }
  }

  async function saveTagSelection(selection: PublishRawTagList) {
    tagSelection.value = selection
    if (!context.value) {
      await initializeContext()
    }
    if (!context.value) return 'staged'

    if (!context.value.gindex) {
      tagSetupState.value = 'staged'
      await persistCurrentTagDraft(context.value)
      await runChecksAction()
      return 'staged'
    }

    progressMap['tag-submit'].status = 'running'
    progressMap['tag-submit'].detail = '提交作品标签'
    await submitGameTags({
      uid: auth.uid,
      sessionId: auth.sessionId,
      gindex: context.value.gindex,
      guid: context.value.guid,
      screenType: form.screenType,
      payType: form.payType,
      tagList: selection,
    }, hooks.onRequest)
    const submittedGuid = context.value.guid
    tagSelection.value = null
    tagSetupState.value = 'none'
    await clearStoredTagDraft(submittedGuid, auth.uid)
    progressMap['tag-submit'].status = 'done'
    progressMap['tag-submit'].detail = '作品标签已提交'
    await runChecksAction()
    return 'submitted'
  }

  async function openTagWebFrame() {
    if (!context.value) {
      await initializeContext()
    }
    if (!context.value) return false
    tagWebOpen.value = true
    return true
  }

  async function clearTagSelection() {
    tagSelection.value = null
    tagSetupState.value = 'none'
    if (!context.value) {
      await initializeContext()
    }
    if (context.value?.guid) {
      await clearStoredTagDraft(context.value.guid, auth.uid)
    }
    await runChecksAction()
  }

  async function openWithTagGuard() {
    await initializeContext(true)
    if (!context.value) return 'panel'
    runningChecks.value = true
    progressMap.checks.status = 'running'
    try {
      checks.value = await runPublishGateChecks(
        context.value,
        {
          stagedTagSelection: tagSelection.value,
          tagSelectionSubmitted: tagSetupState.value === 'submitted',
        },
        hooks,
      )
      progressMap.checks.status = checks.value.some((item) => item.level === 'error') ? 'error' : 'done'
      progressMap.checks.detail = checks.value.length ? `共 ${checks.value.length} 项入口检查结果` : '入口检查通过'
    } finally {
      runningChecks.value = false
    }
    if (checks.value.some((item) => item.key === 'need-tag')) {
      openAfterTagSetup.value = true
      await openTagWebFrame()
      return 'tag-setup'
    }
    const blocking = checks.value.find((item) => item.level === 'error')
    if (blocking) {
      throw new Error(blocking.message || blocking.title)
    }
    return 'panel'
  }

  return {
    loadingContext,
    runningChecks,
    publishing,
    publishLocked,
    context,
    result,
    form,
    checks,
    progressItems,
    uploadMessage,
    hasBlockingChecks,
    tagSelection,
    tagSetupState,
    tagWebOpen,
    openAfterTagSetup,
    initializeContext,
    runChecksAction,
    syncFlowerUnlockFromProject,
    publishAction,
    saveTagSelection,
    clearTagSelection,
    openTagWebFrame,
    openWithTagGuard,
    resetTransientState,
  }
}
